<template>
  <a-card
      :bordered="true"
      class="branch-donut-card"
  >
    <div class="branch-donut-card__header">
      <div>
        <h3 class="branch-donut-card__title">
          {{ title }}
        </h3>

        <p
            v-if="description"
            class="branch-donut-card__description"
        >
          {{ description }}
        </p>

        <p
            v-if="showAdditionalText && additionalText"
            class="branch-donut-card__additional-text"
        >
          {{ additionalText }}
        </p>

      </div>

      <QuestionCircleOutlined class="branch-donut-card__help-icon" />
    </div>

    <div class="branch-donut-card__content">
      <DomainsDonutChart :items="items" class="donut__card" />

      <div class="branch-donut-card__legend">
        <div
            v-for="item in normalizedItems"
            :key="item.key"
            class="branch-donut-card__legend-item"
        >
          <span
              class="branch-donut-card__legend-mark"
              :style="{ backgroundColor: item.color }"
          ></span>

          <span class="branch-donut-card__legend-text">
            {{ item.label }} — {{ formatValue(item.value) }} чел.
          </span>
        </div>
      </div>
    </div>
  </a-card>
</template>

<script>
import { computed } from 'vue'
import { QuestionCircleOutlined } from '@ant-design/icons-vue'
import DomainsDonutChart from "@/components/Analytics/DomainsDonutChart.vue";

const FALLBACK_COLORS = [
  '#6C5CE7',
  '#A29BFE',
  '#00B5AD',
  '#71E5E0',
  '#D5DBE2',
]

export default {
  name: 'BranchDonutCard',

  components: {
    DomainsDonutChart,
    QuestionCircleOutlined,
  },

  props: {
    title: {
      type: String,
      default: '',
    },

    description: {
      type: String,
      default: '',
    },

    items: {
      type: Array,
      default: () => [],
    },

    showAdditionalText: {
      type: Boolean,
      default: false,
    },

    additionalText: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    const normalizedItems = computed(() => {
      return (Array.isArray(props.items) ? props.items : []).map((item, index) => ({
        ...item,
        key: item.key || `item-${index}`,
        label: item.label || '',
        value: toNumber(item.value),
        color: item.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length],
      }))
    })

    const formatValue = (value) => {
      const number = toNumber(value)

      return Number.isInteger(number)
          ? number
          : number.toFixed(1)
    }

    return {
      normalizedItems,
      formatValue,
    }
  },
}

function toNumber(value) {
  const number = Number(value)

  return Number.isFinite(number) ? number : 0
}
</script>

<style scoped lang="scss">
.branch-donut-card {
  min-width: 0;
  height: 100%;
  border-radius: 8px;
}

.branch-donut-card :deep(.ant-card-body) {
  height: 100%;
  padding: 18px 16px 16px;
}

.branch-donut-card__header {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 28px;
}

.branch-donut-card__title {
  margin: 0 0 8px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.branch-donut-card__description {
  margin: 0;
  font-size: 14px;
  line-height: 130%;
  color: rgba(0, 0, 0, 0.45);
}

.branch-donut-card__help-icon {
  flex: 0 0 auto;
  margin-top: 2px;
  color: rgba(0, 0, 0, 0.25);
  font-size: 14px;
}

.branch-donut-card__content {
  display: flex;
  align-items: center;
  gap: 24px;
}

.branch-donut-card__legend {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.branch-donut-card__legend-item {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.branch-donut-card__legend-mark {
  width: 9px;
  height: 9px;
  flex: 0 0 auto;
  border-radius: 2px;
}

.branch-donut-card__legend-text {
  min-width: 0;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.branch-donut-card__additional-text {
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

@media (max-width: $mobile-breakpoint) {
  .branch-donut-card__header {
    margin-bottom: 18px;
  }

  .branch-donut-card__content {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
  }
}

.donut__card {
  width: 128px;
  height: 112px;
  flex: 0 0 128px;
}
</style>
